// ============================================================================
// BLOCK BOOKING MODAL
// ============================================================================
// Cashier-facing modal that transitions a TENTATIVE booking to BLOCKED.
// Reads BLOCKING_POLICY config so the cashier sees what the property allows
// (initial block window, extension policy, max-hold cap), pre-fills a
// sensible default expiry (now + block_duration_hours), and caps the input
// at that same window — extensions happen separately via ExtendBlockModal.
//
// Mirrors ExtendBlockModal in shape and styling.

import React, { useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { bookingService } from 'api/services';
import { useBlockingPolicy } from 'hooks/api/useConfig';
import notificationService from 'services/notificationService';
import type { Booking } from 'api/types/booking.types';

interface BlockBookingModalProps {
  isOpen: boolean;
  booking: Booking | null;
  onClose: () => void;
  onBlocked: () => void;
}

const fmt = (d: Date): string =>
  d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });

// HTML datetime-local needs YYYY-MM-DDTHH:mm in the browser's local timezone.
const toDatetimeLocal = (d: Date): string => {
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 16);
};

const BlockBookingModal: React.FC<BlockBookingModalProps> = ({
  isOpen, booking, onClose, onBlocked,
}) => {
  const { config: policy, refetch: refetchPolicy } = useBlockingPolicy(booking?.propertyId);
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [remarks, setRemarks] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  // Refetch policy on every open — config can change at any time.
  useEffect(() => {
    if (isOpen && booking?.propertyId) refetchPolicy(booking.propertyId);
  }, [isOpen, booking?.propertyId, refetchPolicy]);

  // Default expiry = now + block_duration_hours (24h fallback while policy loads).
  // Re-derives whenever the modal opens or policy resolves so the cashier always
  // sees a deadline computed from "right now", not from when they first opened the modal.
  useEffect(() => {
    if (!isOpen) return;
    const hours = policy?.blockDurationHours ?? 24;
    const seed = new Date(Date.now() + hours * 3600 * 1000);
    setExpiresAt(toDatetimeLocal(seed));
    setRemarks('');
  }, [isOpen, policy?.blockDurationHours]);

  // Caps. Initial-block cap is block_duration_hours (server enforces same).
  // max_block_hold_hours only matters for later extensions; we surface it as
  // info so the cashier knows how much rope they have via Extend later.
  const projection = useMemo(() => {
    if (!isOpen || !booking || !policy) return null;
    const now = new Date();
    const minExpiry = new Date(now.getTime() + 5 * 60 * 1000); // at least 5 minutes from now
    const maxExpiry = new Date(now.getTime() + policy.blockDurationHours * 3600 * 1000);
    const parsed = expiresAt ? new Date(expiresAt) : null;
    const valid = !!parsed && !isNaN(parsed.getTime())
                 && parsed.getTime() > minExpiry.getTime()
                 && parsed.getTime() <= maxExpiry.getTime();
    return { now, minExpiry, maxExpiry, parsed, valid };
  }, [isOpen, booking, policy, expiresAt]);

  if (!isOpen || !booking) return null;

  const blockGuard = (() => {
    if (!policy) return { disabled: true, reason: 'Loading policy…' };
    if (!projection) return { disabled: true, reason: 'Loading…' };
    if (!projection.parsed || isNaN(projection.parsed.getTime()))
      return { disabled: true, reason: 'Pick a valid expiry.' };
    if (projection.parsed <= projection.minExpiry)
      return { disabled: true, reason: 'Expiry must be at least a few minutes in the future.' };
    if (projection.parsed > projection.maxExpiry)
      return {
        disabled: true,
        reason: `Initial block cannot exceed ${policy.blockDurationHours} hours from now (cap: ${fmt(projection.maxExpiry)}).`,
      };
    return { disabled: false, reason: '' };
  })();

  const handleBlock = async () => {
    if (!projection?.parsed) return;
    setSubmitting(true);
    try {
      await bookingService.block(booking.bookingId, projection.parsed.toISOString(), remarks.trim() || undefined);
      notificationService.toast.success(
        `Booking "${booking.bookingNumber}" blocked until ${fmt(projection.parsed)}.`
      );
      onBlocked();
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message ||
        'Failed to block booking.';
      notificationService.toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const ROW: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '180px 1fr',
    gap: '0.75rem',
    fontSize: '0.875rem',
    padding: '0.25rem 0',
  };
  const LABEL: React.CSSProperties = { color: 'var(--text-secondary)' };
  const VALUE: React.CSSProperties = { color: 'var(--text-primary)', fontWeight: 500 };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-sm">
        {submitting && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Blocking…</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Block Booking — {booking.bookingNumber}</h2>
          <button className="modal-close" onClick={onClose} disabled={submitting}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          {/* Policy summary — read-only context for the cashier */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={ROW}>
              <span style={LABEL}>Initial block window</span>
              <span style={VALUE}>{policy ? `${policy.blockDurationHours} hours` : '—'}</span>
            </div>
            <div style={ROW}>
              <span style={LABEL}>Extensions allowed</span>
              <span style={VALUE}>
                {policy
                  ? policy.blockExtensionAllowed
                    ? `Yes — up to ${policy.maxExtensions} × ${policy.extensionHours}h, capped at now + ${policy.maxBlockHoldHours}h`
                    : 'No — only the initial window applies'
                  : '—'}
              </span>
            </div>
            <div style={ROW}>
              <span style={LABEL}>Default expiry</span>
              <span style={VALUE}>
                {projection?.maxExpiry ? fmt(projection.maxExpiry) : '—'}
              </span>
            </div>
          </div>

          {/* Editable expiry input */}
          <div className="form-group" style={{ marginBottom: '0.75rem' }}>
            <label className="form-label">
              Expiry <span className="text-danger">*</span>
            </label>
            <input
              type="datetime-local"
              className="form-input"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              max={projection?.maxExpiry ? toDatetimeLocal(projection.maxExpiry) : undefined}
              min={projection?.minExpiry ? toDatetimeLocal(projection.minExpiry) : undefined}
            />
            {policy && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                Capped at {policy.blockDurationHours}h from now. Use Extend Block later if the policy allows it.
              </div>
            )}
          </div>

          {/* Optional remarks (audited) */}
          <div className="form-group" style={{ marginBottom: '0.5rem' }}>
            <label className="form-label">Remarks</label>
            <textarea
              className="form-textarea"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Optional — why is this booking being held?"
              style={{ resize: 'vertical', minHeight: '50px' }}
            />
          </div>

          {/* Hard-block reason or projected new state */}
          {blockGuard.disabled && blockGuard.reason ? (
            <div
              style={{
                padding: '0.625rem 0.875rem',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                color: 'var(--text-secondary)',
                fontSize: '0.8125rem',
                fontStyle: 'italic',
              }}
            >
              {blockGuard.reason}
            </div>
          ) : (
            projection?.parsed && (
              <div
                style={{
                  padding: '0.625rem 0.875rem',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '0.8125rem',
                }}
              >
                <div style={{ ...ROW, padding: 0 }}>
                  <span style={LABEL}>Booking will be held until</span>
                  <span style={{ ...VALUE, color: '#16a34a' }}>{fmt(projection.parsed)}</span>
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Inventory remains held. The booking auto-cancels after the expiry unless confirmed or extended.
                </div>
              </div>
            )
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleBlock}
            disabled={submitting || blockGuard.disabled}
            title={blockGuard.disabled ? blockGuard.reason : undefined}
          >
            <IconSystem.lock size={16} />
            Block Booking
          </button>
        </div>
      </div>
    </>
  );
};

export default BlockBookingModal;
